import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import BottomNav from '@/components/feature/BottomNav';
import { mockWhiteNoises, mockStats } from '@/mocks/userData';

type FocusMode = 'focus' | 'short' | 'long';
type TimerState = 'idle' | 'running' | 'paused';

const modeDurations: Record<FocusMode, number> = { focus: 25 * 60, short: 5 * 60, long: 15 * 60 };
const modeLabels: Record<FocusMode, string> = { focus: '专注', short: '短休', long: '长休' };

export default function FocusPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<FocusMode>('focus');
  const [timerState, setTimerState] = useState<TimerState>('idle');
  const [remaining, setRemaining] = useState(modeDurations.focus);
  const [selectedNoise, setSelectedNoise] = useState('');
  const [showComplete, setShowComplete] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const total = modeDurations[mode];
  const progress = 1 - remaining / total;
  const radius = 108;
  const circumference = 2 * Math.PI * radius;

  useEffect(() => {
    setRemaining(modeDurations[mode]);
    setTimerState('idle');
  }, [mode]);

  useEffect(() => {
    if (timerState === 'running') {
      intervalRef.current = setInterval(() => {
        setRemaining(r => {
          if (r <= 1) {
            clearInterval(intervalRef.current!);
            setTimerState('idle');
            setShowComplete(true);
            setTimeout(() => setShowComplete(false), 3000);
            return 0;
          }
          return r - 1;
        });
      }, 1000);
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [timerState]);

  const formatTime = (s: number) => {
    const m = Math.floor(s / 60).toString().padStart(2, '0');
    const sec = (s % 60).toString().padStart(2, '0');
    return `${m}:${sec}`;
  };

  const handleStart = () => setTimerState('running');
  const handlePause = () => setTimerState('paused');
  const handleStop = () => { setTimerState('idle'); setRemaining(modeDurations[mode]); };

  const modes: FocusMode[] = ['focus', 'short', 'long'];

  // 蓝紫色热力点阵
  const heatDots = Array.from({ length: 21 }).map((_, i) => {
    const filled = i < Math.floor(mockStats.todayFocusMinutes / 5);
    const opacity = filled ? 0.3 + (i % 4) * 0.15 : 0.1;
    return { filled, opacity };
  });

  return (
    <div className="h-full flex flex-col relative" style={{ background: '#F5F7FA', fontFamily: '\'Noto Sans SC\', sans-serif' }}>
      <div className="flex-1 overflow-y-auto relative" style={{ scrollbarWidth: 'none' }}>
        {/* 背景弥散光 */}
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 70% 50% at 50% 30%, rgba(181,198,224,0.2) 0%, transparent 70%)' }} />

        {/* Header */}
        <div className="flex items-center justify-between px-4 pt-12 pb-2">
          <div className="w-8" />
          <span className="font-semibold text-sm" style={{ color: '#2D2B3D' }}>
            {timerState === 'running' ? '专注中' : '专注'}
          </span>
          <button className="w-8 h-8 flex items-center justify-center cursor-pointer">
            <i className="ri-volume-up-line text-lg" style={{ color: '#C5B9D8' }} />
          </button>
        </div>

        {/* Mode switcher */}
        <div className="flex justify-center gap-6 mb-6 mt-2">
          {modes.map((m) => (
            <button
              key={m}
              onClick={() => setMode(m)}
              className="relative text-sm cursor-pointer whitespace-nowrap transition-all pb-1.5"
              style={{ color: mode === m ? '#A8B8D8' : '#B0B0B0', fontWeight: mode === m ? 600 : 400 }}
            >
              {modeLabels[m]}
              {mode === m && <span className="absolute bottom-0 left-0 right-0 h-0.5 rounded-full" style={{ background: 'linear-gradient(90deg,#B5C6E0,#C5B9D8)' }} />}
            </button>
          ))}
        </div>

        {/* Timer circle */}
        <div className="flex justify-center items-center mb-5 relative">
          <div className="relative" style={{ width: 256, height: 256 }}>
            <div className="absolute inset-0 rounded-full pointer-events-none" style={{ background: 'radial-gradient(ellipse at center, rgba(181,198,224,0.18) 0%, transparent 70%)', filter: 'blur(8px)' }} />
            <svg width="256" height="256" viewBox="0 0 256 256" className="absolute inset-0">
              <defs>
                <linearGradient id="timerGradBlue" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#B5C6E0" />
                  <stop offset="100%" stopColor="#C5B9D8" />
                </linearGradient>
              </defs>
              <circle cx="128" cy="128" r={radius} fill="none" stroke="rgba(181,198,224,0.18)" strokeWidth="10" />
              <circle
                cx="128" cy="128" r={radius} fill="none"
                stroke="url(#timerGradBlue)" strokeWidth="10"
                strokeDasharray={`${circumference * progress} ${circumference}`}
                strokeLinecap="round"
                transform="rotate(-90 128 128)"
                style={{ transition: 'stroke-dasharray 1s linear' }}
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="font-bold tabular-nums" style={{ fontSize: 46, color: '#2D2B3D', letterSpacing: 2, fontFamily: '\'Noto Sans SC\', sans-serif' }}>
                {formatTime(remaining)}
              </span>
              <span className="text-sm mt-1" style={{ color: '#8E8E93' }}>{modeLabels[mode]}</span>
            </div>
          </div>
        </div>

        {/* White noise — 五个按钮等距居中 */}
        <div className="px-4 mb-4">
          <div className="flex items-center justify-between">
            {mockWhiteNoises.map(noise => {
              const isSelected = selectedNoise === noise.id;
              return (
                <button
                  key={noise.id}
                  onClick={() => setSelectedNoise(isSelected ? '' : noise.id)}
                  className="flex flex-col items-center gap-1.5 cursor-pointer active:scale-90 transition-transform"
                >
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center transition-all"
                    style={{
                      background: isSelected ? 'linear-gradient(135deg,#B5C6E0,#C5B9D8)' : 'rgba(255,255,255,0.72)',
                      border: isSelected ? 'none' : '1px solid rgba(181,198,224,0.25)',
                    }}
                  >
                    <i className={`${noise.icon} text-lg`} style={{ color: isSelected ? '#fff' : '#C5B9D8' }} />
                  </div>
                  <span style={{ fontSize: 11, color: isSelected ? '#A8B8D8' : '#B0B0B0' }}>{noise.name}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* 明信片生成入口 */}
        <div className="px-4 mb-4">
          <button
            onClick={() => navigate('/postcard')}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl cursor-pointer active:scale-95 transition-transform"
            style={{
              background: 'rgba(197,185,216,0.15)',
              border: '1px solid rgba(181,198,224,0.25)',
              backdropFilter: 'blur(12px)',
            }}
          >
            <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg,#B5C6E0,#C5B9D8)' }}>
              <i className="ri-mail-line text-white text-base" />
            </div>
            <div className="flex-1 text-left">
              <p className="text-sm font-medium" style={{ color: '#A8B8D8' }}>生成今日专注明信片</p>
              <p className="text-xs mt-0.5" style={{ color: '#C5B9D8' }}>今日已专注 {mockStats.todayFocusMinutes} 分钟</p>
            </div>
            <div className="w-12 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg,#B5C6E0,#C5B9D8)' }}>
              <span className="text-white font-bold" style={{ fontSize: 11 }}>{mockStats.todayFocusMinutes}m</span>
            </div>
          </button>
        </div>

        {/* Buttons */}
        <div className="px-4 flex gap-3">
          {timerState === 'idle' && (
            <button
              onClick={handleStart}
              className="flex-1 rounded-2xl font-semibold text-white cursor-pointer active:scale-95 transition-transform whitespace-nowrap"
              style={{ height: 56, background: 'linear-gradient(135deg,#B5C6E0,#A8B8D8)', fontSize: 17, letterSpacing: 2, boxShadow: '0 8px 24px rgba(181,198,224,0.45)' }}
            >
              开始专注
            </button>
          )}
          {timerState === 'running' && (
            <>
              <button onClick={handlePause} className="flex-1 rounded-2xl font-semibold cursor-pointer active:scale-95 transition-transform whitespace-nowrap" style={{ height: 56, background: 'rgba(255,255,255,0.75)', color: '#A8B8D8', fontSize: 16, border: '1px solid rgba(181,198,224,0.3)' }}>暂停</button>
              <button onClick={handleStop} className="flex-1 rounded-2xl font-semibold cursor-pointer active:scale-95 transition-transform whitespace-nowrap" style={{ height: 56, background: 'rgba(255,255,255,0.75)', color: '#C5B9D8', fontSize: 16, border: '1px solid rgba(197,185,216,0.3)' }}>结束</button>
            </>
          )}
          {timerState === 'paused' && (
            <>
              <button onClick={handleStart} className="flex-1 rounded-2xl font-semibold text-white cursor-pointer active:scale-95 transition-transform whitespace-nowrap" style={{ height: 56, background: 'linear-gradient(135deg,#B5C6E0,#A8B8D8)', fontSize: 16 }}>继续</button>
              <button onClick={handleStop} className="flex-1 rounded-2xl font-semibold cursor-pointer active:scale-95 transition-transform whitespace-nowrap" style={{ height: 56, background: 'rgba(255,255,255,0.75)', color: '#C5B9D8', fontSize: 16, border: '1px solid rgba(197,185,216,0.3)' }}>结束</button>
            </>
          )}
        </div>

        {/* Today summary — 蓝紫色热力点阵 */}
        <div className="px-4 mt-5 mb-4 flex items-center gap-3">
          <span className="text-xs" style={{ color: '#8E8E93' }}>今日已专注 {mockStats.todayFocusMinutes} 分钟</span>
          <div className="flex gap-0.5">
            {heatDots.map((dot, i) => (
              <div
                key={i}
                className="w-2 h-2 rounded-sm"
                style={{ background: dot.filled ? `rgba(181,198,224,${dot.opacity})` : 'rgba(181,198,224,0.1)' }}
              />
            ))}
          </div>
        </div>
      </div>

      <BottomNav />

      {showComplete && (
        <div className="absolute inset-0 flex items-center justify-center z-50 pointer-events-none">
          <div className="flex flex-col items-center gap-3 px-8 py-6 rounded-3xl" style={{ background: 'rgba(255,255,255,0.94)', backdropFilter: 'blur(16px)', border: '1px solid rgba(181,198,224,0.3)' }}>
            <i className="ri-checkbox-circle-fill text-5xl" style={{ color: '#A8B8D8' }} />
            <p className="font-semibold text-base" style={{ color: '#2D2B3D' }}>专注完成！</p>
            <p className="text-sm" style={{ color: '#8E8E93' }}>你真棒，继续保持</p>
          </div>
        </div>
      )}
    </div>
  );
}
